import { useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { Price } from 'src/api/endpoints/cloudConnect.types';

/** Converts ints to float with given precision */
export const convertToFloatWithPrecision = (amount: number, precision = 0) =>
  amount / Math.pow(10, precision);

export function PriceConnect({
  price,
  variant = 'total',
  itemLabel,
  fallback = 'N/A',
  boldPrice = false,
  hidePeriodLabel = false,
  breakBeforePeriodOnMobile = false, // can be removed when checkout v2 is fully rolled out.
  ...props
}: {
  price: Price | null | undefined;
  variant?: 'total' | 'unit';
  itemLabel?: string;
  boldPrice?: boolean;
  hidePeriodLabel?: boolean;
  breakBeforePeriodOnMobile?: boolean;
  fallback?: ReactNode;
} & React.HTMLAttributes<HTMLSpanElement>) {
  const { i18n } = useLingui();
  const amountFormatted = useMemo(() => {
    if (!price) {
      return fallback;
    }
    const floatAmount = convertToFloatWithPrecision(
      variant === 'unit' ? price.unitAmount : price.totalAmount,
      price.precision
    );

    return new Intl.NumberFormat(i18n.locale, {
      style: 'currency',
      currency: price.currency.toUpperCase(),
    }).format(floatAmount);
  }, [price, variant, fallback]);

  const intervalLabel = price?.period === 'annual' ? 'year' : 'month';

  const item = itemLabel;
  const quantity = price?.quantity || 1;
  const quantityLabel =
    quantity > 1 && variant === 'total' ? `for ${quantity}` : 'per';

  if (!price) {
    return (
      <span data-cy="price" {...props}>
        {fallback}
      </span>
    );
  }

  return (
    <span data-cy="price" {...props}>
      <span className={boldPrice ? 'font-semibold' : ''}>
        {amountFormatted}
        {item && ` ${quantityLabel} ${item}`}
      </span>
      {price.period && !hidePeriodLabel && (
        <>
          {breakBeforePeriodOnMobile && <br className="lm:hidden" />}
          <span>/{intervalLabel}</span>
        </>
      )}
    </span>
  );
}
